import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

const BackgroundMusic = () => {
    const { isDark } = useTheme();
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);
    const hasInteracted = useRef(false);

    useEffect(() => {
        // Initialize audio
        const audio = new Audio('/Audio/bgm-keys-of-moon.mp3');
        audio.loop = true;
        audio.volume = 0.38; // 38% volume (between 35-40%)
        audioRef.current = audio;

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const togglePlay = () => {
        if (!audioRef.current) return;

        hasInteracted.current = true; // Ensure they are marked as having interacted

        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play()
                .then(() => setIsPlaying(true))
                .catch(err => console.error("Could not play audio:", err));
        }
    };

    // Determine the color theme based on the current route
    const location = useLocation();

    const getThemeColors = () => {
        const path = location.pathname;

        switch (path) {
            case '/v011-cabin':
                return {
                    dark: 'border-[#E35336]/30 text-[#E35336] hover:border-[#E35336]/60 shadow-[0_0_20px_rgba(227,83,54,0.2)]',
                    light: 'border-[#E35336]/40 text-[#E35336] hover:bg-[#E35336]/5 shadow-[0_0_20px_rgba(227,83,54,0.3)]'
                };
            case '/v012-cabin':
                return {
                    dark: 'border-[#FDFBD4]/30 text-[#FDFBD4] hover:border-[#FDFBD4]/60 shadow-[0_0_20px_rgba(253,251,212,0.15)]',
                    light: 'border-[#FDFBD4]/40 text-[#B8A830] hover:bg-[#FDFBD4]/5 shadow-[0_0_20px_rgba(253,251,212,0.2)]'
                };
            case '/v009-cabin':
                return {
                    dark: 'border-rose-500/30 text-rose-500 hover:border-rose-500/60 shadow-[0_0_20px_rgba(244,63,94,0.2)]',
                    light: 'border-rose-400 text-rose-600 hover:bg-rose-50 shadow-[0_0_20px_rgba(244,63,94,0.3)]'
                };
            case '/v007-cabin':
            case '/v008-cabin':
                return {
                    dark: 'border-amber-500/30 text-amber-500 hover:border-amber-500/60 shadow-[0_0_20px_rgba(245,158,11,0.2)]',
                    light: 'border-amber-400 text-amber-600 hover:bg-amber-50 shadow-[0_0_20px_rgba(245,158,11,0.3)]'
                };
            case '/c-cabin':
                return {
                    dark: 'border-violet-500/30 text-violet-500 hover:border-violet-500/60 shadow-[0_0_20px_rgba(139,92,246,0.2)]',
                    light: 'border-violet-400 text-violet-600 hover:bg-violet-50 shadow-[0_0_20px_rgba(139,92,246,0.3)]'
                };
            case '/sleeping-pod':
                return {
                    dark: 'border-sky-500/30 text-sky-500 hover:border-sky-500/60 shadow-[0_0_20px_rgba(14,165,233,0.2)]',
                    light: 'border-sky-400 text-sky-600 hover:bg-sky-50 shadow-[0_0_20px_rgba(14,165,233,0.3)]'
                };
            case '/dome-house':
                return {
                    dark: 'border-orange-500/30 text-orange-500 hover:border-orange-500/60 shadow-[0_0_20px_rgba(249,115,22,0.2)]',
                    light: 'border-orange-400 text-orange-600 hover:bg-orange-50 shadow-[0_0_20px_rgba(249,115,22,0.3)]'
                };
            default:
                // Default amber theme for landing page and others
                return {
                    dark: 'border-amber-500/30 text-amber-500 hover:border-amber-500/60 shadow-[0_0_20px_rgba(245,158,11,0.2)]',
                    light: 'border-amber-400 text-amber-600 hover:bg-amber-50 shadow-[0_0_20px_rgba(245,158,11,0.3)]'
                };
        }
    };

    const colors = getThemeColors();

    return (
        <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.9 }}
            onClick={togglePlay}
            title={isPlaying ? "Mute Background Music" : "Play Background Music"}
            className={`fixed bottom-6 left-6 z-50 p-3.5 rounded-full shadow-2xl transition-all duration-500 flex items-center justify-center border
                ${isDark
                    ? `bg-black/60 hover:bg-black/80 ${colors.dark}`
                    : `bg-white hover:bg-gray-50 ${colors.light}`
                }`}
        >
            {isPlaying ? (
                <Volume2 className="w-5 h-5 animate-pulse" />
            ) : (
                <VolumeX className="w-5 h-5 opacity-70" />
            )}
        </motion.button>
    );
};

export default BackgroundMusic;
